// Insurer claims: states, settlement and readiness (docs/plans/phase2-private-insurance.md
// §3). A claim is prepared as a draft, submitted on an invoice to the insurer, then paid
// in part or in full by remittances, rejected, or written off with approval.

import type { Money } from '../money.ts';
import { addMoney, formatMoney, subtractMoney } from '../money.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import type { CoverValidity } from './coverage.ts';
import type { InsurerGateState } from './preauth-gate.ts';

export const INSURER_CLAIM_STATES = [
  'draft',
  'submitted',
  'partly_paid',
  'paid',
  'rejected',
  'written_off',
  'entered_in_error',
] as const;
export type InsurerClaimState = (typeof INSURER_CLAIM_STATES)[number];

export interface ClaimAmounts {
  readonly claimed: Money;
  readonly paid: Money;
  readonly writtenOff: Money;
}

/** What the insurer still owes: claimed less paid less written off. */
export function claimOutstanding(a: ClaimAmounts): Money {
  return subtractMoney(a.claimed, addMoney(a.paid, a.writtenOff));
}

/**
 * The state after a payment or a write-off. Nothing outstanding: paid if anything was paid,
 * otherwise written off. Something outstanding: partly paid once anything was paid,
 * otherwise the claim stays where it was (submitted or rejected).
 */
export function settleState(
  current: 'submitted' | 'partly_paid' | 'rejected',
  amounts: ClaimAmounts,
): InsurerClaimState {
  const outstanding = claimOutstanding(amounts);
  if (outstanding.cents < 0n) throw new RangeError('Paid and written off exceed the claim');
  if (outstanding.cents === 0n) return amounts.paid.cents > 0n ? 'paid' : 'written_off';
  if (amounts.paid.cents > 0n) return 'partly_paid';
  return current === 'partly_paid' ? 'submitted' : current;
}

export type InsurerClaimEvent = 'submit' | 'reject' | 'resubmit' | 'enter_in_error';

/**
 * The moves a person makes. Payments and write-offs move a claim through `settleState`.
 * Only a draft can be entered in error: a submitted claim is on an invoice the insurer has,
 * so it is written off instead.
 */
export function insurerClaimTransition(
  state: InsurerClaimState,
  event: InsurerClaimEvent,
): Result<InsurerClaimState, 'insurer_claim.not_allowed'> {
  switch (event) {
    case 'submit':
      return state === 'draft' ? ok('submitted') : err('insurer_claim.not_allowed');
    case 'reject':
      return state === 'submitted' ? ok('rejected') : err('insurer_claim.not_allowed');
    case 'resubmit':
      return state === 'rejected' ? ok('submitted') : err('insurer_claim.not_allowed');
    case 'enter_in_error':
      return state === 'draft' ? ok('entered_in_error') : err('insurer_claim.not_allowed');
  }
}

/** States in which money may still arrive or be written off. */
export const OPEN_INSURER_CLAIM_STATES: readonly InsurerClaimState[] = [
  'submitted',
  'partly_paid',
  'rejected',
];

/** The claim's state as a sentence (DESIGN.md §7: claim states read as sentences). */
export function claimSentence(input: {
  readonly state: InsurerClaimState;
  readonly outstanding: Money;
  readonly rejectionReason: string | null;
}): string {
  switch (input.state) {
    case 'draft':
      return 'Not sent yet';
    case 'submitted':
      return `Sent, waiting for payment of ${formatMoney(input.outstanding)}`;
    case 'partly_paid':
      return `Partly paid: ${formatMoney(input.outstanding)} outstanding`;
    case 'paid':
      return 'Paid';
    case 'rejected':
      return input.rejectionReason === null ? 'Rejected' : `Rejected: ${input.rejectionReason}`;
    case 'written_off':
      return 'Written off';
    case 'entered_in_error':
      return 'Entered in error';
  }
}

// --- Readiness -----------------------------------------------------------------------

export const INSURER_READINESS_CODES = [
  'visit.not_closed',
  'cover.not_valid',
  'cover.no_member_number',
  'line.not_priced',
  'line.preauth_missing',
  'line.preauth_declined',
  'claim.no_lines',
] as const;
export type InsurerReadinessCode = (typeof INSURER_READINESS_CODES)[number];

/** English sentences; each says what to fix. */
export const INSURER_READINESS_WORDS: Record<InsurerReadinessCode, string> = {
  'visit.not_closed': 'The visit is still open. Close it before sending the claim.',
  'cover.not_valid': 'The patient’s cover was not valid on the visit day.',
  'cover.no_member_number': 'The cover has no member number.',
  'line.not_priced': 'A charge has no price yet.',
  'line.preauth_missing': 'A service needs the insurer’s approval, and none is recorded.',
  'line.preauth_declined': 'The insurer declined a service on this claim.',
  'claim.no_lines': 'Nothing on this visit is for the insurer to pay.',
};

export interface InsurerReadinessCharge {
  readonly id: string;
  readonly pricing: 'priced' | 'unpriced';
  readonly payerAmount: Money | null;
  readonly preauth: InsurerGateState;
}

export interface InsurerReadinessProblem {
  readonly code: InsurerReadinessCode;
  readonly subjectId: string | null;
}

/**
 * Every problem that stops a claim being sent, in a stable order (visit, cover, then lines
 * in the order given), and the charges the claim would carry: priced, with an insurer share.
 */
export function checkInsurerClaimReadiness(input: {
  readonly visitClosed: boolean;
  readonly cover: { readonly validity: CoverValidity; readonly memberNumber: string | null };
  readonly charges: readonly InsurerReadinessCharge[];
}): { problems: InsurerReadinessProblem[]; claimableChargeIds: string[] } {
  const problems: InsurerReadinessProblem[] = [];
  if (!input.visitClosed) problems.push({ code: 'visit.not_closed', subjectId: null });
  if (input.cover.validity !== 'valid') problems.push({ code: 'cover.not_valid', subjectId: null });
  if ((input.cover.memberNumber ?? '').trim() === '')
    problems.push({ code: 'cover.no_member_number', subjectId: null });
  const claimable: string[] = [];
  for (const charge of input.charges) {
    if (charge.pricing === 'unpriced') {
      problems.push({ code: 'line.not_priced', subjectId: charge.id });
      continue;
    }
    if (charge.preauth === 'declined')
      problems.push({ code: 'line.preauth_declined', subjectId: charge.id });
    else if (['needs_request', 'waiting', 'expired'].includes(charge.preauth))
      problems.push({ code: 'line.preauth_missing', subjectId: charge.id });
    if (charge.payerAmount !== null && charge.payerAmount.cents > 0n) claimable.push(charge.id);
  }
  if (claimable.length === 0 && !problems.some((p) => p.code === 'line.not_priced'))
    problems.push({ code: 'claim.no_lines', subjectId: null });
  return { problems, claimableChargeIds: claimable };
}

// --- Write-offs ------------------------------------------------------------------------

/** Plan B13, provisional: TODO(business). `other` needs a note. */
export const INSURER_WRITE_OFF_REASONS = [
  'insurer_rejected',
  'past_deadline',
  'not_covered',
  'short_payment',
  'other',
] as const;
export type InsurerWriteOffReason = (typeof INSURER_WRITE_OFF_REASONS)[number];

export const INSURER_WRITE_OFF_REASON_WORDS: Record<InsurerWriteOffReason, string> = {
  insurer_rejected: 'The insurer rejected it for good',
  past_deadline: 'Past the insurer’s deadline',
  not_covered: 'Not covered by the scheme',
  short_payment: 'The insurer paid less and won’t pay the rest',
  other: 'Other',
};
